import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import {
  Plus, X, Search, FileText, Download, Send, Trash2,
  DollarSign, Clock, CheckCircle2, AlertTriangle, ChevronRight,
  Palette, Eye, CreditCard, Building2, Landmark, Mail, MessageSquare,
  Share2, RefreshCw, Package, Link2, Banknote, Bell, Users, Edit3
} from 'lucide-react';
import axios from 'axios';
import Logo from './Logo';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { label: 'Draft', cls: 'bg-gray-500/15 text-gray-400 border-gray-500/30' },
  sent: { label: 'Sent', cls: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  paid: { label: 'Paid', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  overdue: { label: 'Overdue', cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-500/15 text-gray-500 border-gray-500/30' },
};

const TEMPLATES = [
  { id: 'classic', name: 'Classic', desc: 'Traditional professional layout with colored header' },
  { id: 'modern', name: 'Modern', desc: 'Clean design with accent color top border' },
  { id: 'minimal', name: 'Minimal', desc: 'Simple and elegant with minimal styling' },
  { id: 'professional', name: 'Professional', desc: 'Corporate style with full header bar' },
];

const ACCENT_COLORS = ['#7c3aed', '#2563eb', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed', '#ec4899', '#1e293b'];

const CURRENCY_SYMBOLS = { USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$' };

const emptyForm = () => ({
  template: 'classic', from_name: '', from_email: '', from_phone: '', from_address: '',
  to_name: '', to_email: '', to_phone: '', to_address: '', contact_id: '',
  issue_date: new Date().toISOString().split('T')[0], due_date: '',
  items: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
  currency: 'USD', tax_rate: 0, discount_rate: 0,
  bank_details: { account_name: '', bank_name: '', account_number: '', routing_number: '', swift_code: '', iban: '' },
  payment_link: '', accent_color: '#7c3aed', notes: '', terms: '',
  is_recurring: false, recurring_interval: '', recurring_next_date: '', recurring_end_date: '',
});

const Invoices = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, create, detail, preview
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [crmContacts, setCrmContacts] = useState([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: 0, method: 'bank_transfer', date: '', note: '' });
  const [productsList, setProductsList] = useState([]);
  const [shareLink, setShareLink] = useState('');
  const [reminders, setReminders] = useState([]);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [activeTab, setActiveTab] = useState('invoices');
  // Customers tab state
  const [customers, setCustomers] = useState([]);
  const [custLoading, setCustLoading] = useState(false);
  const [custSearch, setCustSearch] = useState('');
  const [editingCust, setEditingCust] = useState(null);
  const [custForm, setCustForm] = useState({ name: '', email: '', phone: '', company: '', notes: '' });
  const [showCustForm, setShowCustForm] = useState(false);
  // Products tab state
  const [products, setProducts] = useState([]);
  const [prodLoading, setProdLoading] = useState(false);
  const [prodSearch, setProdSearch] = useState('');
  const [editingProd, setEditingProd] = useState(null);
  const [prodForm, setProdForm] = useState({ name: '', price: 0, description: '', category: '' });
  const [showProdForm, setShowProdForm] = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  const headers = { Authorization: `Bearer ${token}` };
  const currSym = CURRENCY_SYMBOLS[form.currency] || '$';

  const fetchInvoices = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      if (searchQuery) params.search = searchQuery;
      const res = await axios.get(`${API}/invoices/`, { headers, params });
      setInvoices(res.data);
    } catch { /* */ } finally { setLoading(false); }
  }, [token, statusFilter, searchQuery]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/invoices/stats`, { headers });
      setStats(res.data);
    } catch { /* */ }
  }, [token]);

  useEffect(() => { fetchInvoices(); fetchStats(); }, [fetchInvoices, fetchStats]);

  const fetchCrmContacts = async () => {
    try {
      const res = await axios.get(`${API}/crm/contacts`, { headers, params: { status_filter: 'customer' } });
      setCrmContacts(res.data);
    } catch { /* */ }
  };

  const openCreate = async () => {
    const base = emptyForm();
    // Auto-fill from payment settings
    try {
      const res = await axios.get(`${API}/payment-settings/invoice-defaults`, { headers });
      const d = res.data;
      if (d.from_name) base.from_name = d.from_name;
      if (d.from_email) base.from_email = d.from_email;
      if (d.from_phone) base.from_phone = d.from_phone;
      if (d.from_address) base.from_address = d.from_address;
      if (d.bank_details) base.bank_details = d.bank_details;
      if (d.payment_link) base.payment_link = d.payment_link;
      if (d.currency) base.currency = d.currency;
      if (d.notes) base.notes = d.notes;
      if (d.terms) base.terms = d.terms;
      if (d.tax_rate) base.tax_rate = d.tax_rate;
    } catch { /* use empty defaults */ }
    setForm(base);
    fetchCrmContacts();
    fetchProducts();
    setView('create');
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products/`, { headers });
      setProductsList(res.data);
    } catch { /* */ }
  };

  const addProductAsItem = (product) => {
    setForm(f => ({
      ...f,
      items: [...f.items, { description: product.name, quantity: 1, rate: product.price, amount: product.price }]
    }));
  };

  const [sendingMethod, setSendingMethod] = useState(null);

  const handleSendInvoice = async (method) => {
    if (!selectedInvoice?.id) return;
    setSendingMethod(method);
    try {
      const res = await axios.post(`${API}/invoices/${selectedInvoice.id}/send`, { method }, { headers });
      const delivery = res.data.delivery;
      const recipient = delivery?.recipient || '';
      toast({
        title: `Invoice sent via ${method}!`,
        description: method === 'email'
          ? `Email with PDF delivered to ${recipient}`
          : `${method === 'sms' ? 'SMS' : 'WhatsApp'} sent to ${recipient}`
      });
      setShowSendModal(false);
      const updated = await axios.get(`${API}/invoices/${selectedInvoice.id}`, { headers });
      setSelectedInvoice(updated.data);
      fetchInvoices();
    } catch (err) {
      const detail = err.response?.data?.detail || `Failed to send via ${method}`;
      toast({ title: 'Delivery Failed', description: detail, variant: 'destructive' });
    } finally {
      setSendingMethod(null);
    }
  };

  const handleGetShareLink = async () => {
    if (!selectedInvoice?.id) return;
    try {
      const res = await axios.get(`${API}/invoices/${selectedInvoice.id}/share-link`, { headers });
      const link = `${window.location.origin}/invoice-view/${res.data.invoice_id}?t=${res.data.share_token}`;
      setShareLink(link);
      navigator.clipboard.writeText(link).then(() => toast({ title: 'Link copied to clipboard!' })).catch(() => {});
    } catch { toast({ title: 'Failed to generate link', variant: 'destructive' }); }
  };

  const handleRecordPayment = async () => {
    if (!selectedInvoice?.id || !paymentForm.amount) return;
    try {
      const res = await axios.post(`${API}/invoices/${selectedInvoice.id}/payment`, paymentForm, { headers });
      toast({ title: 'Payment recorded' });
      setSelectedInvoice(res.data);
      setShowPaymentModal(false);
      setPaymentForm({ amount: 0, method: 'bank_transfer', date: '', note: '' });
      fetchInvoices();
      fetchStats();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  const openDetail = async (inv) => {
    try {
      const res = await axios.get(`${API}/invoices/${inv.id}`, { headers });
      setSelectedInvoice(res.data);
      setView('detail');
      // Fetch reminders for this invoice
      try {
        const remRes = await axios.get(`${API}/reminders/invoice/${inv.id}`, { headers });
        setReminders(remRes.data);
      } catch { setReminders([]); }
    } catch { toast({ title: 'Failed to load invoice', variant: 'destructive' }); }
  };

  const handleSendReminder = async (invoiceId) => {
    setSendingReminder(true);
    try {
      const res = await axios.post(`${API}/reminders/send/${invoiceId}`, {}, { headers });
      toast({ title: res.data.success ? 'Reminder sent!' : 'Failed to send reminder' });
      setReminders(prev => [res.data.reminder, ...prev]);
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail, variant: 'destructive' });
    } finally { setSendingReminder(false); }
  };

  const handleDownloadFinalNotice = async (invoiceId) => {
    try {
      const res = await axios.get(`${API}/reminders/final-notice-pdf/${invoiceId}`, {
        headers, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `final-notice-${invoiceId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Failed to download', description: err.response?.data?.detail || 'PDF not available', variant: 'destructive' });
    }
  };

  const openEdit = (inv) => {
    setForm({
      ...inv,
      bank_details: inv.bank_details || { account_name: '', bank_name: '', account_number: '', routing_number: '', swift_code: '', iban: '' },
    });
    fetchCrmContacts();
    setView('create');
    setSelectedInvoice(inv);
  };

  // Calculate totals live
  const calcSubtotal = () => form.items.reduce((sum, i) => sum + (i.quantity * i.rate), 0);
  const calcDiscount = () => calcSubtotal() * (form.discount_rate / 100);
  const calcTax = () => (calcSubtotal() - calcDiscount()) * (form.tax_rate / 100);
  const calcTotal = () => calcSubtotal() - calcDiscount() + calcTax();

  const handleSave = async () => {
    if (!form.to_name.trim()) {
      toast({ title: 'Client name is required', variant: 'destructive' });
      return;
    }
    if (!form.items.length || !form.items[0].description) {
      toast({ title: 'Add at least one item', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: form.items.map(i => ({ ...i, amount: i.quantity * i.rate })),
      };
      if (selectedInvoice?.id) {
        await axios.put(`${API}/invoices/${selectedInvoice.id}`, payload, { headers });
        toast({ title: 'Invoice updated' });
      } else {
        await axios.post(`${API}/invoices/`, payload, { headers });
        toast({ title: 'Invoice created!' });
      }
      setView('list');
      setSelectedInvoice(null);
      fetchInvoices();
      fetchStats();
    } catch (err) {
      toast({ title: 'Failed to save', description: err.response?.data?.detail, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/invoices/${id}`, { headers });
      toast({ title: 'Invoice deleted' });
      setView('list');
      setSelectedInvoice(null);
      fetchInvoices();
      fetchStats();
    } catch { /* */ }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API}/invoices/${id}`, { status: newStatus }, { headers });
      toast({ title: `Status updated to ${newStatus}` });
      if (selectedInvoice?.id === id) {
        const res = await axios.get(`${API}/invoices/${id}`, { headers });
        setSelectedInvoice(res.data);
      }
      fetchInvoices();
      fetchStats();
    } catch { /* */ }
  };

  const handleDownloadPdf = async (id) => {
    try {
      const res = await axios.get(`${API}/invoices/${id}/pdf`, {
        headers, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast({ title: 'PDF downloaded' });
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' });
    }
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { description: '', quantity: 1, rate: 0, amount: 0 }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, field, val) => setForm(f => ({
    ...f, items: f.items.map((item, i) => i === idx ? { ...item, [field]: field === 'description' ? val : (parseFloat(val) || 0) } : item)
  }));
  const updateBank = (field, val) => setForm(f => ({ ...f, bank_details: { ...f.bank_details, [field]: val } }));

  const selectContact = (contactId) => {
    const c = crmContacts.find(ct => ct.id === contactId);
    if (c) {
      setForm(f => ({ ...f, to_name: c.name || '', to_email: c.email || '', to_phone: c.phone || '', to_address: '', contact_id: c.id }));
    }
  };

  // --- Customers tab ---
  const fetchCustomers = useCallback(async () => {
    setCustLoading(true);
    try {
      const params = { status_filter: 'customer' };
      if (custSearch) params.search = custSearch;
      const res = await axios.get(`${API}/crm/contacts`, { headers, params });
      setCustomers(res.data);
    } catch { /* */ } finally { setCustLoading(false); }
  }, [token, custSearch]);

  useEffect(() => { if (activeTab === 'customers') fetchCustomers(); }, [activeTab, fetchCustomers]);

  const handleSaveCust = async () => {
    if (!custForm.name.trim() && !custForm.email.trim()) { toast({ title: 'Name or email required', variant: 'destructive' }); return; }
    try {
      if (editingCust) {
        await axios.put(`${API}/crm/contacts/${editingCust}`, custForm, { headers });
        toast({ title: 'Customer updated' });
      } else {
        await axios.post(`${API}/crm/contacts`, custForm, { headers });
        toast({ title: 'Customer added' });
      }
      setShowCustForm(false); setEditingCust(null); setCustForm({ name: '', email: '', phone: '', company: '', notes: '' });
      fetchCustomers();
    } catch (err) { toast({ title: 'Failed', description: err.response?.data?.detail, variant: 'destructive' }); }
  };

  const handleDeleteCust = async (id) => {
    try { await axios.delete(`${API}/crm/contacts/${id}`, { headers }); toast({ title: 'Customer deleted' }); fetchCustomers(); } catch { toast({ title: 'Failed to delete', variant: 'destructive' }); }
  };

  const startEditCust = (c) => {
    setCustForm({ name: c.name || '', email: c.email || '', phone: c.phone || '', company: c.company || '', notes: c.notes || '' });
    setEditingCust(c.id); setShowCustForm(true);
  };

  // --- Products tab ---
  const fetchProds = useCallback(async () => {
    setProdLoading(true);
    try {
      const res = await axios.get(`${API}/products/`, { headers });
      setProducts(res.data);
    } catch { /* */ } finally { setProdLoading(false); }
  }, [token]);

  useEffect(() => { if (activeTab === 'products') fetchProds(); }, [activeTab, fetchProds]);

  const handleSaveProd = async () => {
    if (!prodForm.name.trim()) { toast({ title: 'Product name required', variant: 'destructive' }); return; }
    try {
      if (editingProd) {
        await axios.put(`${API}/products/${editingProd}`, prodForm, { headers });
        toast({ title: 'Product updated' });
      } else {
        await axios.post(`${API}/products/`, prodForm, { headers });
        toast({ title: 'Product added' });
      }
      setShowProdForm(false); setEditingProd(null); setProdForm({ name: '', price: 0, description: '', category: '' });
      fetchProds();
    } catch (err) { toast({ title: 'Failed', description: err.response?.data?.detail, variant: 'destructive' }); }
  };

  const handleDeleteProd = async (id) => {
    try { await axios.delete(`${API}/products/${id}`, { headers }); toast({ title: 'Product deleted' }); fetchProds(); } catch { toast({ title: 'Failed to delete', variant: 'destructive' }); }
  };

  const startEditProd = (p) => {
    setProdForm({ name: p.name || '', price: p.price || 0, description: p.description || '', category: p.category || '' });
    setEditingProd(p.id); setShowProdForm(true);
  };

  const filteredProds = products.filter(p => !prodSearch || p.name?.toLowerCase().includes(prodSearch.toLowerCase()) || p.category?.toLowerCase().includes(prodSearch.toLowerCase()));

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628]" data-testid="invoices-page">
      <header className="border-b border-white/5 bg-[#0a1628]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo linkTo="/dashboard" />
            <div className="h-6 w-px bg-white/10" />
            <nav className="flex items-center gap-1" data-testid="invoice-tabs">
              {[
                { id: 'invoices', label: 'Invoices', icon: FileText },
                { id: 'customers', label: 'Customers', icon: Users },
                { id: 'products', label: 'Products', icon: Package },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setView('list'); setSelectedInvoice(null); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-purple-600/20 text-white' : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'}`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-3.5 h-3.5" /> {tab.label}
                </button>
              ))}
            </nav>
          </div>
          {view === 'list' && activeTab === 'invoices' && (
            <Button onClick={openCreate} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="create-invoice-btn">
              <Plus className="w-4 h-4 mr-2" /> New Invoice
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">

        {/* ====== LIST VIEW ====== */}
        {view === 'list' && activeTab === 'invoices' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="invoice-stats">
              {[
                { label: 'Total Revenue', value: `$${(stats.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: 'text-emerald-400' },
                { label: 'Outstanding', value: `$${(stats.outstanding || 0).toLocaleString()}`, icon: Clock, color: 'text-blue-400' },
                { label: 'Overdue', value: `$${(stats.overdue || 0).toLocaleString()}`, icon: AlertTriangle, color: 'text-red-400' },
                { label: 'Total Invoices', value: stats.total_invoices || 0, icon: FileText, color: 'text-purple-400' },
              ].map(s => (
                <div key={s.label} className="bg-white/[0.03] border border-white/10 rounded-xl p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center`}>
                    <s.icon className={`w-5 h-5 ${s.color}`} />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-white">{s.value}</p>
                    <p className="text-[10px] text-gray-500">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search invoices..." className="bg-gray-900/50 border-white/10 text-white pl-10 h-9" data-testid="invoice-search" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-gray-900/50 border-white/10 text-white w-36 h-9" data-testid="invoice-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-white/10">
                  <SelectItem value="all" className="text-white">All Status</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                    <SelectItem key={k} value={k} className="text-white">{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Invoice List */}
            <div className="space-y-2" data-testid="invoice-list">
              {loading ? (
                <div className="text-center py-16 text-gray-400">Loading invoices...</div>
              ) : invoices.length === 0 ? (
                <Card className="bg-gray-900/50 border-white/10">
                  <CardContent className="py-16 text-center">
                    <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2" data-testid="no-invoices-msg">No invoices yet</h3>
                    <p className="text-gray-400 mb-4">Create your first professional invoice.</p>
                    <Button onClick={openCreate} className="bg-purple-600 hover:bg-purple-700 text-white">
                      <Plus className="w-4 h-4 mr-2" /> Create Invoice
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                invoices.map(inv => {
                  const sc = STATUS_CONFIG[inv.status] || STATUS_CONFIG.draft;
                  const cs = CURRENCY_SYMBOLS[inv.currency] || '$';
                  return (
                    <div key={inv.id} onClick={() => openDetail(inv)} className="bg-gray-900/50 border border-white/10 hover:border-purple-500/30 rounded-lg p-4 cursor-pointer transition-all flex items-center gap-4" data-testid={`invoice-row-${inv.id}`}>
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: inv.accent_color + '20' }}>
                        <FileText className="w-5 h-5" style={{ color: inv.accent_color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-white font-medium text-sm">{inv.invoice_number}</p>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${sc.cls}`}>{sc.label}</span>
                          {inv.final_notice_sent && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full border bg-red-500/15 text-red-400 border-red-500/30" data-testid={`final-notice-list-badge-${inv.id}`}>Final Notice</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{inv.to_name}</span>
                          <span>{inv.issue_date}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <p className="text-white font-bold">{cs}{inv.total?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        {/* ====== CUSTOMERS TAB ====== */}
        {activeTab === 'customers' && view === 'list' && (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input value={custSearch} onChange={e => setCustSearch(e.target.value)} placeholder="Search customers..." className="bg-gray-900/50 border-white/10 text-white pl-10 h-9" data-testid="cust-search" />
              </div>
              <Button onClick={() => { setShowCustForm(true); setEditingCust(null); setCustForm({ name: '', email: '', phone: '', company: '', notes: '' }); }} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-customer-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Customer
              </Button>
            </div>

            {/* Customer Form Modal */}
            {showCustForm && (
              <Card className="bg-gray-900/50 border-white/10 mb-4" data-testid="cust-form">
                <CardContent className="pt-5 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-gray-400 text-xs">Name *</Label><Input value={custForm.name} onChange={e => setCustForm(f => ({...f, name: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" data-testid="cust-name" /></div>
                    <div><Label className="text-gray-400 text-xs">Email</Label><Input value={custForm.email} onChange={e => setCustForm(f => ({...f, email: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" data-testid="cust-email" /></div>
                    <div><Label className="text-gray-400 text-xs">Phone</Label><Input value={custForm.phone} onChange={e => setCustForm(f => ({...f, phone: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" data-testid="cust-phone" /></div>
                    <div><Label className="text-gray-400 text-xs">Company</Label><Input value={custForm.company} onChange={e => setCustForm(f => ({...f, company: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" data-testid="cust-company" /></div>
                  </div>
                  <div><Label className="text-gray-400 text-xs">Notes</Label><Input value={custForm.notes} onChange={e => setCustForm(f => ({...f, notes: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" /></div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => { setShowCustForm(false); setEditingCust(null); }} className="border-white/10 text-gray-400">Cancel</Button>
                    <Button onClick={handleSaveCust} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-cust-btn">{editingCust ? 'Update' : 'Add'} Customer</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="space-y-2" data-testid="customers-list">
              {custLoading ? (
                <div className="text-center py-16 text-gray-400">Loading customers...</div>
              ) : customers.length === 0 ? (
                <div className="text-center py-16 text-gray-500">No customers yet. Add your first customer above.</div>
              ) : (
                <div className="bg-gray-900/30 border border-white/10 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs">Name</th>
                        <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs">Email</th>
                        <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs">Phone</th>
                        <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs">Company</th>
                        <th className="text-right px-4 py-3 text-gray-400 font-medium text-xs w-28">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customers.map(c => (
                        <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`cust-row-${c.id}`}>
                          <td className="px-4 py-3 text-white font-medium">{c.name || '—'}</td>
                          <td className="px-4 py-3 text-gray-400">{c.email || '—'}</td>
                          <td className="px-4 py-3 text-gray-400">{c.phone || '—'}</td>
                          <td className="px-4 py-3 text-gray-400">{c.company || '—'}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => startEditCust(c)} className="text-gray-400 hover:text-blue-400 h-7 w-7 p-0" data-testid={`edit-cust-${c.id}`}>
                                <Edit3 className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handleDeleteCust(c.id)} className="text-gray-400 hover:text-red-400 h-7 w-7 p-0" data-testid={`delete-cust-${c.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ====== PRODUCTS TAB ====== */}
        {activeTab === 'products' && view === 'list' && (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input value={prodSearch} onChange={e => setProdSearch(e.target.value)} placeholder="Search products..." className="bg-gray-900/50 border-white/10 text-white pl-10 h-9" data-testid="prod-search" />
              </div>
              <Button onClick={() => { setShowProdForm(true); setEditingProd(null); setProdForm({ name: '', price: 0, description: '', category: '' }); }} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-product-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Product
              </Button>
            </div>

            {/* Product Form Modal */}
            {showProdForm && (
              <Card className="bg-gray-900/50 border-white/10 mb-4" data-testid="prod-form">
                <CardContent className="pt-5 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-gray-400 text-xs">Product Name *</Label><Input value={prodForm.name} onChange={e => setProdForm(f => ({...f, name: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" data-testid="prod-name" /></div>
                    <div><Label className="text-gray-400 text-xs">Price</Label><Input type="number" min="0" step="0.01" value={prodForm.price} onChange={e => setProdForm(f => ({...f, price: parseFloat(e.target.value) || 0}))} className="bg-black/30 border-white/10 text-white h-9" data-testid="prod-price" /></div>
                    <div><Label className="text-gray-400 text-xs">Category</Label><Input value={prodForm.category} onChange={e => setProdForm(f => ({...f, category: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" data-testid="prod-category" /></div>
                    <div><Label className="text-gray-400 text-xs">Description</Label><Input value={prodForm.description} onChange={e => setProdForm(f => ({...f, description: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" data-testid="prod-desc" /></div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => { setShowProdForm(false); setEditingProd(null); }} className="border-white/10 text-gray-400">Cancel</Button>
                    <Button onClick={handleSaveProd} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-prod-btn">{editingProd ? 'Update' : 'Add'} Product</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="space-y-2" data-testid="products-list">
              {prodLoading ? (
                <div className="text-center py-16 text-gray-400">Loading products...</div>
              ) : filteredProds.length === 0 ? (
                <div className="text-center py-16 text-gray-500">No products yet. Add your first product above.</div>
              ) : (
                <div className="bg-gray-900/30 border border-white/10 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs">Product</th>
                        <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs">Category</th>
                        <th className="text-right px-4 py-3 text-gray-400 font-medium text-xs">Price</th>
                        <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs">Description</th>
                        <th className="text-right px-4 py-3 text-gray-400 font-medium text-xs w-28">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProds.map(p => (
                        <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`prod-row-${p.id}`}>
                          <td className="px-4 py-3 text-white font-medium">{p.name}</td>
                          <td className="px-4 py-3 text-gray-400">{p.category || '—'}</td>
                          <td className="px-4 py-3 text-emerald-400 text-right font-mono">${p.price?.toFixed(2)}</td>
                          <td className="px-4 py-3 text-gray-400 truncate max-w-[200px]">{p.description || '—'}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => startEditProd(p)} className="text-gray-400 hover:text-blue-400 h-7 w-7 p-0" data-testid={`edit-prod-${p.id}`}>
                                <Edit3 className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handleDeleteProd(p.id)} className="text-gray-400 hover:text-red-400 h-7 w-7 p-0" data-testid={`delete-prod-${p.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ====== CREATE/EDIT VIEW ====== */}
        {view === 'create' && (
          <div className="max-w-4xl mx-auto space-y-6" data-testid="invoice-form">
            {/* Template & Color */}
            <Card className="bg-gray-900/50 border-white/10">
              <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Palette className="w-4 h-4 text-purple-400" /> Template & Style</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {TEMPLATES.map(t => (
                    <button key={t.id} onClick={() => setForm(f => ({...f, template: t.id}))} className={`p-3 rounded-lg border text-left transition-all ${form.template === t.id ? 'border-purple-500/50 bg-purple-500/10' : 'border-white/10 bg-black/20 hover:border-white/20'}`} data-testid={`template-${t.id}`}>
                      <p className="text-white text-sm font-medium">{t.name}</p>
                      <p className="text-gray-500 text-[10px] mt-0.5">{t.desc}</p>
                    </button>
                  ))}
                </div>
                <div>
                  <Label className="text-gray-400 text-xs mb-2 block">Accent Color</Label>
                  <div className="flex gap-2">
                    {ACCENT_COLORS.map((c, i) => (
                      <button key={i} onClick={() => setForm(f => ({...f, accent_color: c}))} className={`w-8 h-8 rounded-full border-2 transition-all ${form.accent_color === c ? 'border-white scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} data-testid={`color-${i}`} />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* From */}
              <Card className="bg-gray-900/50 border-white/10">
                <CardHeader><CardTitle className="text-white text-sm flex items-center gap-2"><Building2 className="w-4 h-4 text-blue-400" /> From (Your Business)</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div><Label className="text-gray-400 text-xs">Business Name</Label><Input value={form.from_name} onChange={e => setForm(f => ({...f, from_name: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" data-testid="from-name" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-gray-400 text-xs">Email</Label><Input value={form.from_email} onChange={e => setForm(f => ({...f, from_email: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" /></div>
                    <div><Label className="text-gray-400 text-xs">Phone</Label><Input value={form.from_phone} onChange={e => setForm(f => ({...f, from_phone: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" /></div>
                  </div>
                  <div><Label className="text-gray-400 text-xs">Address</Label><Input value={form.from_address} onChange={e => setForm(f => ({...f, from_address: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" /></div>
                </CardContent>
              </Card>

              {/* To */}
              <Card className="bg-gray-900/50 border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white text-sm flex items-center gap-2"><Send className="w-4 h-4 text-emerald-400" /> Bill To (Client)</CardTitle>
                    {crmContacts.length > 0 && (
                      <Select onValueChange={selectContact}>
                        <SelectTrigger className="bg-black/30 border-white/10 text-white h-7 w-44 text-xs"><SelectValue placeholder="From CRM..." /></SelectTrigger>
                        <SelectContent className="bg-gray-900 border-white/10 max-h-40">
                          {crmContacts.map(c => <SelectItem key={c.id} value={c.id} className="text-white text-xs">{c.name || c.email}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div><Label className="text-gray-400 text-xs">Client Name *</Label><Input value={form.to_name} onChange={e => setForm(f => ({...f, to_name: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" data-testid="to-name" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-gray-400 text-xs">Email</Label><Input value={form.to_email} onChange={e => setForm(f => ({...f, to_email: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" /></div>
                    <div><Label className="text-gray-400 text-xs">Phone</Label><Input value={form.to_phone} onChange={e => setForm(f => ({...f, to_phone: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" /></div>
                  </div>
                  <div><Label className="text-gray-400 text-xs">Address</Label><Input value={form.to_address} onChange={e => setForm(f => ({...f, to_address: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" /></div>
                </CardContent>
              </Card>
            </div>

            {/* Dates & Currency */}
            <Card className="bg-gray-900/50 border-white/10">
              <CardContent className="pt-5">
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-gray-400 text-xs">Issue Date</Label><Input type="date" value={form.issue_date} onChange={e => setForm(f => ({...f, issue_date: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" data-testid="issue-date" /></div>
                  <div><Label className="text-gray-400 text-xs">Due Date</Label><Input type="date" value={form.due_date} onChange={e => setForm(f => ({...f, due_date: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" data-testid="due-date" /></div>
                  <div>
                    <Label className="text-gray-400 text-xs">Currency</Label>
                    <Select value={form.currency} onValueChange={v => setForm(f => ({...f, currency: v}))}>
                      <SelectTrigger className="bg-black/30 border-white/10 text-white h-9"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-gray-900 border-white/10">
                        {Object.keys(CURRENCY_SYMBOLS).map(c => <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card className="bg-gray-900/50 border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white text-base">Line Items</CardTitle>
                  <div className="flex gap-2">
                    <div className="relative">
                      <Button size="sm" onClick={() => { fetchProducts(); setShowProductPicker(!showProductPicker); }} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="browse-products-btn">
                        <Package className="w-3 h-3 mr-1" /> Products
                      </Button>
                      {showProductPicker && (
                        <div className="absolute right-0 top-full mt-1 w-72 bg-gray-900 border border-white/10 rounded-lg shadow-xl z-20 overflow-hidden" data-testid="product-picker">
                          <div className="p-2 border-b border-white/10">
                            <Input value={pickerSearch} onChange={e => setPickerSearch(e.target.value)} placeholder="Search products..." className="bg-black/30 border-white/10 text-white h-8 text-xs" data-testid="picker-search" />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {productsList.filter(p => !pickerSearch || p.name?.toLowerCase().includes(pickerSearch.toLowerCase())).length === 0 ? (
                              <p className="text-gray-500 text-xs p-3 text-center">No products found</p>
                            ) : (
                              productsList.filter(p => !pickerSearch || p.name?.toLowerCase().includes(pickerSearch.toLowerCase())).map(p => (
                                <button key={p.id} onClick={() => { addProductAsItem(p); setShowProductPicker(false); setPickerSearch(''); }} className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-white/5 transition-colors border-b border-white/5 last:border-0" data-testid={`pick-product-${p.id}`}>
                                  <span className="text-white text-xs font-medium truncate">{p.name}</span>
                                  <span className="text-emerald-400 text-xs font-mono ml-2">${p.price?.toFixed(2)}</span>
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <Button size="sm" onClick={addItem} className="bg-purple-600 hover:bg-purple-700 text-white text-xs" data-testid="add-item-btn">
                      <Plus className="w-3 h-3 mr-1" /> Add Item
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Header */}
                <div className="grid grid-cols-[1fr_80px_100px_100px_32px] gap-2 mb-2 px-1">
                  <span className="text-gray-500 text-xs">Description</span>
                  <span className="text-gray-500 text-xs text-right">Qty</span>
                  <span className="text-gray-500 text-xs text-right">Rate</span>
                  <span className="text-gray-500 text-xs text-right">Amount</span>
                  <span />
                </div>
                {form.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_80px_100px_100px_32px] gap-2 mb-2" data-testid={`item-row-${idx}`}>
                    <Input value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} placeholder="Item description" className="bg-black/30 border-white/10 text-white h-9 text-sm" data-testid={`item-desc-${idx}`} />
                    <Input type="number" min="0" step="0.01" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} className="bg-black/30 border-white/10 text-white h-9 text-sm text-right" />
                    <Input type="number" min="0" step="0.01" value={item.rate} onChange={e => updateItem(idx, 'rate', e.target.value)} className="bg-black/30 border-white/10 text-white h-9 text-sm text-right" />
                    <div className="flex items-center justify-end text-white text-sm font-mono h-9 bg-black/20 rounded-md px-2 border border-white/5">
                      {currSym}{(item.quantity * item.rate).toFixed(2)}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => removeItem(idx)} className="text-gray-500 hover:text-red-400 h-9 w-8 p-0" disabled={form.items.length <= 1}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}

                {/* Totals */}
                <div className="mt-4 border-t border-white/10 pt-4 space-y-2 max-w-xs ml-auto">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Subtotal</span>
                    <span className="text-white font-mono">{currSym}{calcSubtotal().toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm gap-2">
                    <span className="text-gray-400">Discount</span>
                    <div className="flex items-center gap-1">
                      <Input type="number" min="0" max="100" step="0.5" value={form.discount_rate} onChange={e => setForm(f => ({...f, discount_rate: parseFloat(e.target.value) || 0}))} className="bg-black/30 border-white/10 text-white h-7 w-16 text-xs text-right" data-testid="discount-rate" />
                      <span className="text-gray-500 text-xs">%</span>
                      <span className="text-red-400 font-mono ml-2">-{currSym}{calcDiscount().toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm gap-2">
                    <span className="text-gray-400">Tax</span>
                    <div className="flex items-center gap-1">
                      <Input type="number" min="0" max="100" step="0.5" value={form.tax_rate} onChange={e => setForm(f => ({...f, tax_rate: parseFloat(e.target.value) || 0}))} className="bg-black/30 border-white/10 text-white h-7 w-16 text-xs text-right" data-testid="tax-rate" />
                      <span className="text-gray-500 text-xs">%</span>
                      <span className="text-white font-mono ml-2">{currSym}{calcTax().toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-white/10">
                    <span className="text-white">Total</span>
                    <span className="text-white" data-testid="invoice-total">{currSym}{calcTotal().toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notes & Terms */}
            <Card className="bg-gray-900/50 border-white/10">
              <CardContent className="pt-5 space-y-3">
                <div><Label className="text-gray-400 text-xs">Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Thank you for your business..." className="bg-black/30 border-white/10 text-white min-h-[60px]" /></div>
                <div><Label className="text-gray-400 text-xs">Terms & Conditions</Label><Textarea value={form.terms} onChange={e => setForm(f => ({...f, terms: e.target.value}))} placeholder="Payment is due within 30 days..." className="bg-black/30 border-white/10 text-white min-h-[60px]" /></div>
              </CardContent>
            </Card>

            {/* Recurring Invoice */}
            <Card className="bg-gray-900/50 border-white/10">
              <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><RefreshCw className="w-4 h-4 text-cyan-400" /> Recurring Invoice</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_recurring} onChange={e => setForm(f => ({...f, is_recurring: e.target.checked}))} className="rounded border-white/20" data-testid="recurring-toggle" />
                  <span className="text-gray-300 text-sm">Make this a recurring invoice</span>
                </label>
                {form.is_recurring && (
                  <div className="grid grid-cols-3 gap-3 mt-2">
                    <div>
                      <Label className="text-gray-400 text-xs">Interval</Label>
                      <Select value={form.recurring_interval} onValueChange={v => setForm(f => ({...f, recurring_interval: v}))}>
                        <SelectTrigger className="bg-black/30 border-white/10 text-white h-9"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent className="bg-gray-900 border-white/10">
                          <SelectItem value="weekly" className="text-white">Weekly</SelectItem>
                          <SelectItem value="monthly" className="text-white">Monthly</SelectItem>
                          <SelectItem value="quarterly" className="text-white">Quarterly</SelectItem>
                          <SelectItem value="yearly" className="text-white">Yearly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-gray-400 text-xs">Next Date</Label><Input type="date" value={form.recurring_next_date} onChange={e => setForm(f => ({...f, recurring_next_date: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" /></div>
                    <div><Label className="text-gray-400 text-xs">End Date (optional)</Label><Input type="date" value={form.recurring_end_date} onChange={e => setForm(f => ({...f, recurring_end_date: e.target.value}))} className="bg-black/30 border-white/10 text-white h-9" /></div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { setView('list'); setSelectedInvoice(null); }} className="border-white/10 text-gray-400 hover:text-white flex-1">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white flex-1" data-testid="save-invoice-btn">
                {saving ? 'Saving...' : (selectedInvoice ? 'Update Invoice' : 'Create Invoice')}
              </Button>
            </div>
          </div>
        )}

        {/* ====== DETAIL VIEW ====== */}
        {view === 'detail' && selectedInvoice && (() => {
          const inv = selectedInvoice;
          const sc = STATUS_CONFIG[inv.status] || STATUS_CONFIG.draft;
          const cs = CURRENCY_SYMBOLS[inv.currency] || '$';
          return (
            <div className="max-w-4xl mx-auto space-y-6" data-testid="invoice-detail">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h2 className="text-2xl font-bold text-white" data-testid="invoice-number">{inv.invoice_number}</h2>
                    <span className={`text-xs px-2.5 py-1 rounded-full border ${sc.cls}`}>{sc.label}</span>
                    {inv.final_notice_sent && (
                      <span className="text-xs px-2.5 py-1 rounded-full border bg-red-500/15 text-red-400 border-red-500/30 font-medium" data-testid="final-notice-badge">Final Notice</span>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm">To: {inv.to_name} | Issued: {inv.issue_date}{inv.due_date ? ` | Due: ${inv.due_date}` : ''}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => setShowSendModal(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="send-invoice-btn">
                    <Send className="w-3.5 h-3.5 mr-1.5" /> Send
                  </Button>
                  <Button size="sm" onClick={handleGetShareLink} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="share-link-btn">
                    <Link2 className="w-3.5 h-3.5 mr-1.5" /> Link
                  </Button>
                  <Button size="sm" onClick={() => handleDownloadPdf(inv.id)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="download-pdf-btn">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
                  </Button>
                  <Button size="sm" onClick={() => { setPaymentForm({ amount: inv.total - (inv.payment_records || []).reduce((s, p) => s + p.amount, 0), method: 'bank_transfer', date: new Date().toISOString().split('T')[0], note: '' }); setShowPaymentModal(true); }} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="record-payment-btn">
                    <Banknote className="w-3.5 h-3.5 mr-1.5" /> Payment
                  </Button>
                  {inv.status !== 'paid' && (
                    <Button size="sm" onClick={() => handleStatusChange(inv.id, 'paid')} className="bg-green-600 hover:bg-green-700 text-white" data-testid="mark-paid-btn">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark Paid
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(inv)} className="border-white/10 text-gray-300 hover:text-white" data-testid="edit-invoice-btn">
                    Edit
                  </Button>
                  {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                    <Button size="sm" onClick={() => handleSendReminder(inv.id)} disabled={sendingReminder} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="send-reminder-btn">
                      <Bell className="w-3.5 h-3.5 mr-1.5" /> {sendingReminder ? 'Sending...' : 'Remind'}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => handleDelete(inv.id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="delete-invoice-btn">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {/* Status Actions */}
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-xs mr-2">Set status:</span>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <button key={k} onClick={() => handleStatusChange(inv.id, k)} className={`text-[10px] px-3 py-1 rounded-full border transition-all ${inv.status === k ? v.cls + ' font-bold' : 'border-white/10 text-gray-500 hover:border-white/20'}`} data-testid={`set-status-${k}`}>
                    {v.label}
                  </button>
                ))}
              </div>

              {/* Invoice Preview Card */}
              <Card className="bg-white border-0 overflow-hidden">
                <CardContent className="p-0">
                  {/* Header */}
                  <div className="p-8 pb-0" style={{ borderTop: `4px solid ${inv.accent_color}` }}>
                    <div className="flex justify-between items-start mb-8">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">INVOICE</h3>
                        <p className="text-gray-500 text-sm mt-1">{inv.invoice_number}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-500 text-sm">Date: {inv.issue_date}</p>
                        {inv.due_date && <p className="text-gray-500 text-sm">Due: {inv.due_date}</p>}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-8 mb-8">
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">From</p>
                        <p className="text-gray-900 font-semibold">{inv.from_name || 'Your Business'}</p>
                        {inv.from_email && <p className="text-gray-500 text-sm">{inv.from_email}</p>}
                        {inv.from_phone && <p className="text-gray-500 text-sm">{inv.from_phone}</p>}
                        {inv.from_address && <p className="text-gray-500 text-sm">{inv.from_address}</p>}
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Bill To</p>
                        <p className="text-gray-900 font-semibold">{inv.to_name}</p>
                        {inv.to_email && <p className="text-gray-500 text-sm">{inv.to_email}</p>}
                        {inv.to_phone && <p className="text-gray-500 text-sm">{inv.to_phone}</p>}
                        {inv.to_address && <p className="text-gray-500 text-sm">{inv.to_address}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Items Table */}
                  <div className="px-8">
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ backgroundColor: inv.accent_color + '15' }}>
                          <th className="text-left px-4 py-2 text-gray-700 font-medium text-xs">Description</th>
                          <th className="text-right px-4 py-2 text-gray-700 font-medium text-xs">Qty</th>
                          <th className="text-right px-4 py-2 text-gray-700 font-medium text-xs">Rate</th>
                          <th className="text-right px-4 py-2 text-gray-700 font-medium text-xs">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(inv.items || []).map((item, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                            <td className="px-4 py-2 text-gray-800">{item.description}</td>
                            <td className="px-4 py-2 text-gray-600 text-right">{item.quantity}</td>
                            <td className="px-4 py-2 text-gray-600 text-right">{cs}{item.rate?.toFixed(2)}</td>
                            <td className="px-4 py-2 text-gray-800 text-right font-medium">{cs}{item.amount?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Totals */}
                  <div className="px-8 py-4 flex justify-end">
                    <div className="w-64 space-y-1 text-sm">
                      <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{cs}{inv.subtotal?.toFixed(2)}</span></div>
                      {inv.discount_rate > 0 && <div className="flex justify-between text-gray-600"><span>Discount ({inv.discount_rate}%)</span><span className="text-red-600">-{cs}{inv.discount_amount?.toFixed(2)}</span></div>}
                      {inv.tax_rate > 0 && <div className="flex justify-between text-gray-600"><span>Tax ({inv.tax_rate}%)</span><span>{cs}{inv.tax_amount?.toFixed(2)}</span></div>}
                      <div className="flex justify-between font-bold text-gray-900 pt-2 border-t text-lg" style={{ borderColor: inv.accent_color }}>
                        <span>Total</span><span>{cs}{inv.total?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Payment & Notes */}
                  <div className="px-8 pb-8 space-y-4">
                    {inv.notes && <div><p className="text-xs font-medium text-gray-700 mb-1">Notes</p><p className="text-sm text-gray-600 whitespace-pre-line">{inv.notes}</p></div>}
                    {inv.terms && <div><p className="text-xs font-medium text-gray-700 mb-1">Terms & Conditions</p><p className="text-sm text-gray-500 whitespace-pre-line">{inv.terms}</p></div>}
                  </div>
                </CardContent>
              </Card>

              {/* Reminder History */}
              {reminders.length > 0 && (
                <Card className="bg-gray-900/50 border-white/10" data-testid="reminder-history">
                  <CardHeader>
                    <CardTitle className="text-white text-sm flex items-center gap-2"><Bell className="w-4 h-4 text-orange-400" /> Reminder History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {reminders.map((r, i) => (
                        <div key={r.id || i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${r.type === 'final_notice' ? 'bg-red-500' : r.success ? 'bg-emerald-400' : 'bg-red-400'}`} />
                            <div>
                              <p className="text-sm text-gray-300">
                                {r.type === 'final_notice' ? (
                                  <span className="text-red-400 font-medium">Final Notice</span>
                                ) : r.type === 'manual' ? 'Manual reminder' : `Auto reminder (${r.type?.replace('auto_', '').replace('d', ' days')})`}
                              </p>
                              <p className="text-xs text-gray-500">Sent to {r.recipient} via {r.channel}{r.days_overdue ? ` (${r.days_overdue}d overdue)` : ''}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {r.type === 'final_notice' && (
                              <Button size="sm" variant="ghost" onClick={() => handleDownloadFinalNotice(r.invoice_id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2" data-testid="download-final-notice-pdf">
                                <Download className="w-3.5 h-3.5 mr-1" /> PDF
                              </Button>
                            )}
                            <div className="text-right">
                              <p className={`text-xs font-medium ${r.type === 'final_notice' ? 'text-red-400' : r.success ? 'text-emerald-400' : 'text-red-400'}`}>
                                {r.type === 'final_notice' ? 'Escalated' : r.success ? 'Delivered' : 'Failed'}
                              </p>
                              <p className="text-[10px] text-gray-500">{r.sent_at ? new Date(r.sent_at).toLocaleString() : ''}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          );
        })()}
      </main>

      {/* Send Invoice Modal */}
      {showSendModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-6" data-testid="send-modal">
          <Card className="bg-gray-900 border-white/10 w-full max-w-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Send Invoice</CardTitle>
                <Button size="sm" variant="ghost" onClick={() => setShowSendModal(false)} className="text-gray-400 h-8 w-8 p-0"><X className="w-4 h-4" /></Button>
              </div>
              <p className="text-gray-500 text-xs">{selectedInvoice.invoice_number} to {selectedInvoice.to_name}</p>
            </CardHeader>
            <CardContent className="space-y-2">
              <button onClick={() => handleSendInvoice('email')} disabled={!selectedInvoice.to_email || sendingMethod === 'email'} className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${selectedInvoice.to_email && sendingMethod !== 'email' ? 'border-white/10 hover:border-blue-500/30 cursor-pointer' : 'border-white/5 opacity-50 cursor-not-allowed'}`} data-testid="send-email-btn">
                <div className="w-10 h-10 rounded-lg bg-blue-500/15 flex items-center justify-center"><Mail className="w-5 h-5 text-blue-400" /></div>
                <div className="text-left flex-1"><p className="text-white text-sm font-medium">{sendingMethod === 'email' ? 'Sending email...' : 'Email with PDF'}</p><p className="text-gray-500 text-xs">{selectedInvoice.to_email || 'No email address'}</p></div>
                {sendingMethod === 'email' && <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />}
              </button>
              <button onClick={() => handleSendInvoice('sms')} disabled={!selectedInvoice.to_phone || sendingMethod === 'sms'} className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${selectedInvoice.to_phone && sendingMethod !== 'sms' ? 'border-white/10 hover:border-emerald-500/30 cursor-pointer' : 'border-white/5 opacity-50 cursor-not-allowed'}`} data-testid="send-sms-btn">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center"><MessageSquare className="w-5 h-5 text-emerald-400" /></div>
                <div className="text-left flex-1"><p className="text-white text-sm font-medium">{sendingMethod === 'sms' ? 'Sending SMS...' : 'SMS with Payment Link'}</p><p className="text-gray-500 text-xs">{selectedInvoice.to_phone || 'No phone number'}</p></div>
                {sendingMethod === 'sms' && <div className="w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />}
              </button>
              <button onClick={() => handleSendInvoice('whatsapp')} disabled={!selectedInvoice.to_phone || sendingMethod === 'whatsapp'} className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${selectedInvoice.to_phone && sendingMethod !== 'whatsapp' ? 'border-white/10 hover:border-green-500/30 cursor-pointer' : 'border-white/5 opacity-50 cursor-not-allowed'}`} data-testid="send-whatsapp-btn">
                <div className="w-10 h-10 rounded-lg bg-green-500/15 flex items-center justify-center"><Share2 className="w-5 h-5 text-green-400" /></div>
                <div className="text-left flex-1"><p className="text-white text-sm font-medium">{sendingMethod === 'whatsapp' ? 'Sending...' : 'WhatsApp'}</p><p className="text-gray-500 text-xs">{selectedInvoice.to_phone || 'No phone number'}</p></div>
                {sendingMethod === 'whatsapp' && <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />}
              </button>
              {(selectedInvoice.sent_via || []).length > 0 && (
                <div className="pt-2 border-t border-white/10">
                  <p className="text-gray-500 text-xs">Previously sent via: {selectedInvoice.sent_via.join(', ')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Record Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-6" data-testid="payment-modal">
          <Card className="bg-gray-900 border-white/10 w-full max-w-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Record Payment</CardTitle>
                <Button size="sm" variant="ghost" onClick={() => setShowPaymentModal(false)} className="text-gray-400 h-8 w-8 p-0"><X className="w-4 h-4" /></Button>
              </div>
              <p className="text-gray-500 text-xs">{selectedInvoice.invoice_number} — Total: ${selectedInvoice.total?.toFixed(2)}</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div><Label className="text-gray-400 text-xs">Amount *</Label><Input type="number" min="0" step="0.01" value={paymentForm.amount} onChange={e => setPaymentForm(f => ({...f, amount: parseFloat(e.target.value) || 0}))} className="bg-black/30 border-white/10 text-white" data-testid="payment-amount" /></div>
              <div>
                <Label className="text-gray-400 text-xs">Method</Label>
                <Select value={paymentForm.method} onValueChange={v => setPaymentForm(f => ({...f, method: v}))}>
                  <SelectTrigger className="bg-black/30 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-900 border-white/10">
                    <SelectItem value="bank_transfer" className="text-white">Bank Transfer</SelectItem>
                    <SelectItem value="stripe" className="text-white">Stripe</SelectItem>
                    <SelectItem value="paypal" className="text-white">PayPal</SelectItem>
                    <SelectItem value="cash" className="text-white">Cash</SelectItem>
                    <SelectItem value="check" className="text-white">Check</SelectItem>
                    <SelectItem value="other" className="text-white">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-gray-400 text-xs">Date</Label><Input type="date" value={paymentForm.date} onChange={e => setPaymentForm(f => ({...f, date: e.target.value}))} className="bg-black/30 border-white/10 text-white" /></div>
              <div><Label className="text-gray-400 text-xs">Note</Label><Input value={paymentForm.note} onChange={e => setPaymentForm(f => ({...f, note: e.target.value}))} placeholder="Optional note..." className="bg-black/30 border-white/10 text-white" /></div>
              {(selectedInvoice.payment_records || []).length > 0 && (
                <div className="pt-2 border-t border-white/10">
                  <p className="text-gray-500 text-xs mb-2">Previous payments:</p>
                  {selectedInvoice.payment_records.map((p, i) => (
                    <div key={i} className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>${p.amount?.toFixed(2)} via {p.method}</span>
                      <span>{p.date ? new Date(p.date).toLocaleDateString() : ''}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-xs font-medium text-white mt-1 pt-1 border-t border-white/5">
                    <span>Remaining</span>
                    <span>${(selectedInvoice.total - selectedInvoice.payment_records.reduce((s, p) => s + p.amount, 0)).toFixed(2)}</span>
                  </div>
                </div>
              )}
              <Button onClick={handleRecordPayment} className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="confirm-payment-btn">Record Payment</Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Share Link Toast */}
      {shareLink && (
        <div className="fixed bottom-6 right-6 bg-gray-900 border border-white/10 rounded-lg p-4 z-50 max-w-sm shadow-xl" data-testid="share-link-toast">
          <div className="flex items-center justify-between mb-2">
            <p className="text-white text-sm font-medium flex items-center gap-2"><Link2 className="w-4 h-4 text-purple-400" /> Shareable Link</p>
            <Button size="sm" variant="ghost" onClick={() => setShareLink('')} className="text-gray-400 h-6 w-6 p-0"><X className="w-3 h-3" /></Button>
          </div>
          <p className="text-gray-400 text-xs break-all">{shareLink}</p>
          <p className="text-emerald-400 text-[10px] mt-1">Copied to clipboard!</p>
        </div>
      )}
    </div>
  );
};

export default Invoices;
